import { useState, useEffect } from 'react';

interface DiaryEntry {
  id: string;
  date: string;
  title: string;
  content: string;
  mood: 'great' | 'good' | 'neutral' | 'bad' | 'scary';
  tags: string[];
  luckType: '길몽' | '흉몽' | '보통';
  createdAt: string;
}

const moodConfig = {
  great: { label: '최고', icon: 'ri-emotion-laugh-fill', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
  good: { label: '좋음', icon: 'ri-emotion-fill', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  neutral: { label: '보통', icon: 'ri-emotion-normal-fill', color: 'text-purple-300', bg: 'bg-purple-500/10 border-purple-500/30' },
  bad: { label: '불안', icon: 'ri-emotion-unhappy-fill', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
  scary: { label: '악몽', icon: 'ri-emotion-sad-fill', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
};

const luckColors = {
  길몽: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  흉몽: 'text-red-400 bg-red-500/10 border-red-500/30',
  보통: 'text-purple-300 bg-purple-500/10 border-purple-500/30',
};

const STORAGE_KEY = 'destiny_dream_diary';

function loadEntries(): DiaryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DiaryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveEntries(entries: DiaryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch { /* ignore */ }
}

const defaultEntries: DiaryEntry[] = [
  {
    id: 'demo-1',
    date: '2026-03-15',
    title: '황금 돼지를 만난 꿈',
    content: '거대한 황금빛 돼지가 나타나 나를 바라보더니 입에서 황금 동전을 쏟아냈다. 주변이 눈부시게 빛났고, 온몸에 따뜻한 기운이 느껴졌다.',
    mood: 'great',
    tags: ['돼지', '황금', '재물'],
    luckType: '길몽',
    createdAt: '2026-03-15T08:20:00Z',
  },
  {
    id: 'demo-2',
    date: '2026-03-12',
    title: '하늘을 나는 꿈',
    content: '어느 순간 발이 땅에서 떨어지더니 하늘 높이 솟아올랐다. 구름 위를 유영하며 아래를 내려다보니 온 도시가 보였다. 무서움 없이 오히려 자유롭고 상쾌했다.',
    mood: 'great',
    tags: ['비행', '자유', '하늘'],
    luckType: '길몽',
    createdAt: '2026-03-12T07:45:00Z',
  },
  {
    id: 'demo-3',
    date: '2026-03-08',
    title: '이빨이 빠지는 악몽',
    content: '갑자기 앞니가 흔들리더니 하나씩 빠지기 시작했다. 손으로 잡으려 했지만 계속 빠져나갔다. 잠에서 깨고 나서도 한동안 불안함이 남았다.',
    mood: 'scary',
    tags: ['이빨', '불안'],
    luckType: '흉몽',
    createdAt: '2026-03-08T06:30:00Z',
  },
];

export default function DreamDiary() {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<DiaryEntry | null>(null);
  const [filter, setFilter] = useState<'all' | '길몽' | '흉몽' | '보통'>('all');
  const [tagInput, setTagInput] = useState('');

  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    title: '',
    content: '',
    mood: 'neutral' as DiaryEntry['mood'],
    tags: [] as string[],
    luckType: '보통' as DiaryEntry['luckType'],
  });

  // Load from localStorage once (with demo data if empty)
  useEffect(() => {
    const stored = loadEntries();
    if (stored.length === 0) {
      setEntries(defaultEntries);
      saveEntries(defaultEntries);
    } else {
      setEntries(stored);
    }
    setIsLoaded(true);
  }, []);

  const handleAddTag = () => {
    const t = tagInput.trim().replace(/^#/, '');
    if (t && !form.tags.includes(t)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag: string) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const handleSubmit = () => {
    if (!form.title.trim() || !form.content.trim()) return;
    const newEntry: DiaryEntry = {
      id: `entry-${Date.now()}`,
      date: form.date,
      title: form.title.trim(),
      content: form.content.trim(),
      mood: form.mood,
      tags: form.tags,
      luckType: form.luckType,
      createdAt: new Date().toISOString(),
    };
    const updated = [newEntry, ...entries];
    setEntries(updated);
    saveEntries(updated);
    setForm({ date: new Date().toISOString().split('T')[0], title: '', content: '', mood: 'neutral', tags: [], luckType: '보통' });
    setTagInput('');
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const updated = entries.filter((e) => e.id !== id);
    setEntries(updated);
    saveEntries(updated);
    if (selectedEntry?.id === id) setSelectedEntry(null);
  };

  const filtered = filter === 'all' ? entries : entries.filter((e) => e.luckType === filter);
  const sorted = [...filtered].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (!isLoaded) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-book-2-fill text-primary-gold text-xl"></i>
            </div>
            꿈 일기
          </h2>
          <p className="text-purple-300/60 text-sm mt-1">날짜별로 꿈을 기록하고 길흉을 확인하세요</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter chips */}
          <div className="flex gap-2">
            {(['all', '길몽', '흉몽', '보통'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  filter === f
                    ? 'bg-primary-gold text-black'
                    : 'bg-white/5 border border-white/10 text-purple-300 hover:border-primary-gold/40'
                }`}
              >
                {f === 'all' ? '전체' : f}
              </button>
            ))}
          </div>

          <button
            onClick={() => { setShowForm(true); setSelectedEntry(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-gold text-black text-sm font-bold hover:bg-amber-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm"></i>
            </div>
            <span>꿈 기록</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Entry list */}
        <div className="lg:col-span-2 space-y-3">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-gold/10 border border-primary-gold/20 mb-4">
                <i className="ri-moon-foggy-fill text-primary-gold text-3xl"></i>
              </div>
              <p className="text-purple-300/60 text-sm">아직 기록된 꿈이 없습니다</p>
              <p className="text-purple-400/40 text-xs mt-1">오늘 꾼 꿈을 기록해보세요</p>
            </div>
          ) : (
            sorted.map((entry) => {
              const mood = moodConfig[entry.mood];
              return (
                <div
                  key={entry.id}
                  onClick={() => { setSelectedEntry(entry); setShowForm(false); }}
                  className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 group ${
                    selectedEntry?.id === entry.id
                      ? 'border-primary-gold/60 bg-primary-gold/10'
                      : 'border-white/8 bg-white/3 hover:border-primary-gold/30 hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${mood.icon} ${mood.color} text-base`}></i>
                      </div>
                      <p className="text-white text-sm font-semibold truncate">{entry.title}</p>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${luckColors[entry.luckType]}`}>
                      {entry.luckType}
                    </span>
                  </div>
                  <p className="text-purple-300/60 text-xs leading-relaxed line-clamp-2 mb-2 pl-8">{entry.content}</p>
                  <div className="flex items-center justify-between pl-8">
                    <div className="flex gap-1 flex-wrap">
                      {entry.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full">#{tag}</span>
                      ))}
                    </div>
                    <span className="text-purple-500/60 text-[10px] flex-shrink-0">{entry.date}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail / Form panel */}
        <div className="lg:col-span-3">
          {/* Add form */}
          {showForm && (
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/90 to-mystic-dark/90 border border-primary-gold/30">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-edit-line text-primary-gold text-sm"></i>
                  </div>
                  새 꿈 기록
                </h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 text-purple-400 hover:text-white transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>

              <div className="space-y-4">
                {/* Date */}
                <div>
                  <label className="text-purple-300 text-xs mb-1.5 block">날짜</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm focus:border-primary-gold focus:outline-none"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="text-purple-300 text-xs mb-1.5 block">꿈 제목 <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="예: 황금 용을 만난 꿈"
                    className="w-full px-4 py-2.5 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm placeholder-gray-500 focus:border-primary-gold focus:outline-none"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="text-purple-300 text-xs mb-1.5 block">꿈 내용 <span className="text-red-400">*</span></label>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder="꿈의 내용을 자세히 기록하세요..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm placeholder-gray-500 focus:border-primary-gold focus:outline-none resize-none"
                  />
                  <p className="text-purple-500/60 text-xs mt-1 text-right">{form.content.length}/500</p>
                </div>

                {/* Mood & Luck row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-purple-300 text-xs mb-1.5 block">꿈의 분위기</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(Object.entries(moodConfig) as [DiaryEntry['mood'], typeof moodConfig[keyof typeof moodConfig]][]).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => setForm((prev) => ({ ...prev, mood: key }))}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap ${
                            form.mood === key ? `${cfg.bg} ${cfg.color} border` : 'bg-white/5 border border-white/10 text-purple-400 hover:border-white/20'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${cfg.icon} text-xs`}></i>
                          </div>
                          <span>{cfg.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-purple-300 text-xs mb-1.5 block">길흉</label>
                    <div className="flex gap-2">
                      {(['길몽', '흉몽', '보통'] as const).map((l) => (
                        <button
                          key={l}
                          onClick={() => setForm((prev) => ({ ...prev, luckType: l }))}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                            form.luckType === l ? luckColors[l] : 'bg-white/5 border-white/10 text-purple-400 hover:border-white/20'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="text-purple-300 text-xs mb-1.5 block">태그</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder="태그 입력 후 Enter"
                      className="flex-1 px-4 py-2 rounded-xl bg-primary-dark/70 border border-primary-gold/30 text-white text-sm placeholder-gray-500 focus:border-primary-gold focus:outline-none"
                    />
                    <button
                      onClick={handleAddTag}
                      className="px-3 py-2 rounded-xl bg-primary-gold/20 border border-primary-gold/40 text-primary-gold text-sm hover:bg-primary-gold/30 transition-all cursor-pointer whitespace-nowrap"
                    >
                      추가
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {form.tags.map((tag) => (
                      <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-gold/10 border border-primary-gold/25 text-primary-gold text-xs">
                        #{tag}
                        <button onClick={() => handleRemoveTag(tag)} className="w-3 h-3 flex items-center justify-center hover:text-white transition-colors cursor-pointer">
                          <i className="ri-close-line text-[10px]"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!form.title.trim() || !form.content.trim()}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-bold text-sm hover:shadow-lg hover:shadow-amber-500/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  꿈 일기 저장
                </button>
              </div>
            </div>
          )}

          {/* Entry detail */}
          {selectedEntry && !showForm && (() => {
            const mood = moodConfig[selectedEntry.mood];
            return (
              <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/90 to-mystic-dark/90 border border-primary-gold/30 animate-fadeIn">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${mood.icon} ${mood.color} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-base">{selectedEntry.title}</h3>
                      <p className="text-purple-400/60 text-xs">{selectedEntry.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${luckColors[selectedEntry.luckType]}`}>
                      {selectedEntry.luckType}
                    </span>
                    <button
                      onClick={() => handleDelete(selectedEntry.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-red-400/50 hover:text-red-400 transition-all cursor-pointer"
                    >
                      <i className="ri-delete-bin-6-line text-sm"></i>
                    </button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white/3 border border-white/8 mb-4">
                  <p className="text-purple-200 text-sm leading-relaxed whitespace-pre-wrap">{selectedEntry.content}</p>
                </div>

                {selectedEntry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {selectedEntry.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full bg-primary-gold/10 border border-primary-gold/25 text-primary-gold text-xs font-medium">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-500/20">
                  <p className="text-indigo-300 text-xs font-semibold mb-1 flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-moon-fill text-xs"></i>
                    </div>
                    AI 해몽 메모
                  </p>
                  <p className="text-purple-200/80 text-xs leading-relaxed">
                    {selectedEntry.luckType === '길몽'
                      ? `"${selectedEntry.tags[0] ?? '이 꿈'}" 꿈은 긍정적인 기운의 상징입니다. 현재 대운과 연결해보면 새로운 기회나 재물이 찾아올 징조로 해석됩니다.`
                      : selectedEntry.luckType === '흉몽'
                      ? `이 꿈은 경고의 메시지를 담고 있습니다. 건강 관리와 주변 인간관계를 점검해보는 것을 권장합니다.`
                      : `중립적인 꿈입니다. 잠재의식이 현재 상황을 정리하는 과정에서 나타난 것으로, 특별한 길흉을 의미하지 않습니다.`}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Empty state */}
          {!showForm && !selectedEntry && (
            <div className="flex flex-col items-center justify-center h-full min-h-52 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-gold/5 border border-primary-gold/15 mb-4">
                <i className="ri-moon-clear-line text-primary-gold/40 text-3xl"></i>
              </div>
              <p className="text-purple-300/40 text-sm">왼쪽 목록에서 꿈을 선택하거나</p>
              <p className="text-purple-300/40 text-xs mt-1">새 꿈 기록 버튼으로 추가하세요</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
